import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/students/classes/`;

// Enhanced error handling function
const handleApiError = (error, thunkAPI) => {
  console.log('🔍 Handling API error:', error);
  
  const errorData = error.response?.data;
  const status = error.response?.status;
  
  console.log('Error status:', status);
  console.log('Error data:', errorData);
  console.log('Full error data structure:', JSON.stringify(errorData, null, 2));
  
  let errorMessage = 'حدث خطأ في العملية';
  let fieldErrors = {};
  
  if (status === 404) {
    errorMessage = 'الصف المطلوب غير موجود';
  } else if (status === 403) {
    errorMessage = 'ليس لديك صلاحية لتعديل هذا الصف';
  } else if (status === 401) {
    errorMessage = 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى';
  } else if (errorData) {
    // Handle validation errors with field-specific messages
    if (errorData.name) {
      if (Array.isArray(errorData.name)) {
        fieldErrors.name = errorData.name[0];
        console.log('Name error extracted:', errorData.name[0]);
      } else {
        fieldErrors.name = errorData.name;
      }
    }
    
    if (errorData.teacher) {
      if (Array.isArray(errorData.teacher)) {
        fieldErrors.teacher = errorData.teacher[0];
        console.log('Teacher error extracted:', errorData.teacher[0]);
      } else {
        fieldErrors.teacher = errorData.teacher;
      }
    }
    
    // Handle general error messages
    if (errorData.detail) {
      errorMessage = errorData.detail;
    } else if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
      errorMessage = errorData.non_field_errors[0];
    } else if (typeof errorData === 'string') {
      errorMessage = errorData;
    } else if (Object.keys(fieldErrors).length > 0) {
      // If we have field errors, use a generic message
      errorMessage = 'يرجى تصحيح الأخطاء في الحقول المحددة';
    }
  }
  
  const result = {
    message: errorMessage,
    fieldErrors,
    originalError: errorData,
    status
  };
  
  console.log('🔍 Error handling result:', result);
  console.log('Field errors extracted:', fieldErrors);
  
  return thunkAPI.rejectWithValue(result);
};

// Async Thunks
export const fetchClasses = createAsyncThunk(
  'classes/fetchClasses', 
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(BASE_URL);
      return response.data.map(c => ({
        ...c,
        studentsCount: c.student_count,
        teacher: c.teacher || 'غير محدد',
      }));
    } catch (error) {
      return handleApiError(error, thunkAPI);
    }
  }
);

export const createClass = createAsyncThunk(
  'classes/createClass',
  async (newClass, thunkAPI) => {
    try {
      console.log('🔄 API Call - Creating class:', newClass);
      const response = await axios.post(`${BASE_URL}`, newClass);
      console.log('✅ Create API Response:', response.data);
      return {
        ...response.data,
        studentsCount: 0,
        teacher: response.data.teacher || 'غير محدد',
      };
    } catch (error) {
      console.error('❌ Create API Error:', error);
      return handleApiError(error, thunkAPI);
    }
  }
);

export const updateClass = createAsyncThunk(
  'classes/updateClass',
  async ({ id, data }, thunkAPI) => {
    try {
      console.log('🔄 API Call - Updating class:', { id, data });
      const response = await axios.put(`${BASE_URL}${id}/`, data);
      console.log('✅ Update API Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Update API Error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      return handleApiError(error, thunkAPI);
    }
  }
);

export const deleteClass = createAsyncThunk(
  'classes/deleteClass',
  async (id, thunkAPI) => {
    try {
      console.log('🔄 API Call - Deleting class:', id);
      await axios.delete(`${BASE_URL}${id}/`);
      console.log('✅ Delete successful');
      return id;
    } catch (error) {
      console.error('❌ Delete API Error:', error);
      return handleApiError(error, thunkAPI);
    }
  }
);

export const fetchClassById = createAsyncThunk(
  'classes/fetchClassById',
  async (id, thunkAPI) => {
    try {
      console.log('🔄 API Call - Fetching class by ID:', id);
      const response = await axios.get(`${BASE_URL}${id}/`);
      console.log('✅ Fetch by ID API Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Fetch by ID API Error:', error);
      return handleApiError(error, thunkAPI);
    }
  }
);

// Classes Slice
const classesSlice = createSlice({
  name: 'classes',
  initialState: {
    list: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    
    // Selected class (for detail view)
    selectedClass: null,
    selectedClassStatus: 'idle',
    selectedClassError: null,
    
    // Operation-specific errors
    deleteError: null,
    createError: null,
    updateError: null,
    
    // Field-specific validation errors
    fieldErrors: {},
    
    // Loading states for specific operations
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
  reducers: {
    // Error clearing actions
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    clearCreateError: (state) => {
      state.createError = null;
      state.fieldErrors = {};
    },
    clearUpdateError: (state) => {
      state.updateError = null;
      state.fieldErrors = {};
    },
    clearAllErrors: (state) => {
      state.deleteError = null;
      state.createError = null;
      state.updateError = null;
      state.fieldErrors = {};
      state.error = null;
      state.selectedClassError = null;
    },
    
    // Reset selected class
    clearSelectedClass: (state) => {
      state.selectedClass = null;
      state.selectedClassStatus = 'idle';
      state.selectedClassError = null;
    },
    
    // Reset entire state (useful for logout)
    resetClassesState: () => {
      return {
        list: [],
        status: 'idle',
        error: null,
        selectedClass: null,
        selectedClassStatus: 'idle',
        selectedClassError: null,
        deleteError: null,
        createError: null,
        updateError: null,
        fieldErrors: {},
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Classes
      .addCase(fetchClasses.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.list = action.payload;
        state.error = null;
      })
      .addCase(fetchClasses.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || action.error.message;
        console.log('Fetch classes failed:', action.payload);
      })
      
      // Create Class
      .addCase(createClass.pending, (state) => {
        state.isCreating = true;
        state.createError = null;
        state.fieldErrors = {};
      })
      .addCase(createClass.fulfilled, (state, action) => {
        state.isCreating = false;
        state.list.push(action.payload);
        state.createError = null;
        state.fieldErrors = {};
      })
      .addCase(createClass.rejected, (state, action) => {
        state.isCreating = false;
        console.log('Create class rejected with payload:', action.payload);
        state.createError = action.payload;
        state.fieldErrors = action.payload?.fieldErrors || {};
      })
      
      // Update Class
      .addCase(updateClass.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
        state.fieldErrors = {};
      })
      .addCase(updateClass.fulfilled, (state, action) => {
        state.isUpdating = false;
        const updated = action.payload;
        const index = state.list.findIndex(c => c.id === updated.id);
        
        if (index !== -1) {
          // Preserve existing properties that might not be returned
          state.list[index] = {
            ...state.list[index],
            ...updated,
            studentsCount: updated.student_count || state.list[index].studentsCount,
            teacher: updated.teacher || 'غير محدد',
          };
        }
        
        // Update selected class if it's the same one
        if (state.selectedClass && state.selectedClass.id === updated.id) {
          state.selectedClass = {
            ...state.selectedClass,
            ...updated,
          };
        }
        
        state.updateError = null;
        state.fieldErrors = {};
      })
      .addCase(updateClass.rejected, (state, action) => {
        state.isUpdating = false;
        console.log('Update class rejected with payload:', action.payload);
        state.updateError = action.payload;
        state.fieldErrors = action.payload?.fieldErrors || {};
      })
      
      // Delete Class
      .addCase(deleteClass.pending, (state) => {
        state.isDeleting = true;
        state.deleteError = null;
      })
      .addCase(deleteClass.fulfilled, (state, action) => {
        state.isDeleting = false;
        const deletedId = action.payload;
        state.list = state.list.filter(c => c.id !== deletedId);
        
        // Clear selected class if it was deleted
        if (state.selectedClass && state.selectedClass.id === deletedId) {
          state.selectedClass = null;
          state.selectedClassStatus = 'idle';
        }
        
        state.deleteError = null;
      })
      .addCase(deleteClass.rejected, (state, action) => {
        state.isDeleting = false;
        console.log('Delete class rejected with payload:', action.payload);
        state.deleteError = action.payload;
      })
      
      // Fetch Class By ID
      .addCase(fetchClassById.pending, (state) => {
        state.selectedClassStatus = 'loading';
        state.selectedClass = null;
        state.selectedClassError = null;
      })
      .addCase(fetchClassById.fulfilled, (state, action) => {
        state.selectedClassStatus = 'succeeded';
        state.selectedClass = action.payload;
        state.selectedClassError = null;
      })
      .addCase(fetchClassById.rejected, (state, action) => {
        state.selectedClassStatus = 'failed';
        state.selectedClassError = action.payload?.message || action.error.message;
        console.log('Fetch class by ID failed:', action.payload);
      });
  },
});

// Export actions
export const { 
  clearDeleteError, 
  clearCreateError, 
  clearUpdateError, 
  clearAllErrors,
  clearSelectedClass,
  resetClassesState
} = classesSlice.actions;

// Selectors (optional - you can use these in your components)
export const selectClasses = (state) => state.classes.list;
export const selectClassesStatus = (state) => state.classes.status;
export const selectClassesError = (state) => state.classes.error;
export const selectSelectedClass = (state) => state.classes.selectedClass;
export const selectCreateError = (state) => state.classes.createError;
export const selectUpdateError = (state) => state.classes.updateError;
export const selectDeleteError = (state) => state.classes.deleteError;
export const selectFieldErrors = (state) => state.classes.fieldErrors;
export const selectIsCreating = (state) => state.classes.isCreating;
export const selectIsUpdating = (state) => state.classes.isUpdating;
export const selectIsDeleting = (state) => state.classes.isDeleting;

// Compound selectors
export const selectClassById = (state, classId) => 
  state.classes.list.find(c => c.id === classId);

export const selectActiveClasses = (state) => 
  state.classes.list.filter(c => !c.is_archived);

export const selectClassesByTeacher = (state, teacherId) => 
  state.classes.list.filter(c => c.teacher_id === teacherId);

export default classesSlice.reducer;